<!-- Column settings dropdown content (always in view so ViewChild is available when externalized) -->
<ng-template #columnSettingsTpl>
  <tui-data-list [style.width.rem]="16">
    <ng-container *ngFor="let col of columns">
      <ng-container *ngIf="col.field !== 'actions'">
        <label tuiOption>
          {{ col.label || '' | translate }}
          <input
            tuiCheckbox
            type="checkbox"
            [(ngModel)]="col.visible"
            [disabled]="col.alwaysVisible ?? false"
            (ngModelChange)="onColumnToggle(col)"
          />
        </label>
      </ng-container>
    </ng-container>
  </tui-data-list>
</ng-template>

<ng-template #pageSizeMenuTpl>
  <tui-data-list [style.width.rem]="5">
    <button
      *ngFor="let n of pageSizeItems"
      tuiOption
      type="button"
      (click)="onPageSizeFromMenu(n)"
    >
      {{ n }}
    </button>
  </tui-data-list>
</ng-template>

<!-- Settings button row (only when table shows its own settings) -->
<div
  *ngIf="showSettings && data?.length"
  class="gw-flex gw-justify-between gw-items-center gw-flex-wrap"
>
  <button
    appearance="flat"
    tuiChevron
    tuiIconButton
    type="button"
    [tuiAppearanceState]="settingsMenuOpen ? 'hover' : null"
    [tuiDropdown]="columnSettingsTpl"
    [tuiDropdownMaxHeight]="500"
    [(tuiDropdownOpen)]="settingsMenuOpen"
  >
    <gatewee-icon icon="@phi.gear-six" size="16px" weight="bold"></gatewee-icon>
  </button>
</div>

<!-- Table Container -->
<div
  *ngIf="data?.length"
  class="gw-overflow-x-auto gw-bg-background-elevation-2 gw-p-4 gw-border-base gw-rounded-2xl gw-overflow-hidden"
>
  <div
    *ngIf="showSelectionToolbar()"
    class="gw-table-selection-toolbar gw-mb-3 gw-flex gw-flex-wrap gw-items-center gw-justify-between gw-gap-3 gw-rounded-lg gw-border gw-border-border-normal gw-bg-background-elevation-1 gw-px-4 gw-py-2.5"
  >
    <div
      class="gw-flex gw-min-w-0 gw-flex-1 gw-flex-wrap gw-items-center gw-gap-x-2 gw-gap-y-1 gw-text-sm gw-text-text-secondary"
    >
      <ng-container [ngSwitch]="bannerState()">
        <ng-container *ngSwitchCase="'page'">
          <span>
            {{
              'common.bulk.banner.pageSelected'
                | translate : {count: data.length}
            }}
          </span>
          <button
            type="button"
            class="gw-border-0 gw-bg-transparent gw-p-0 gw-font-medium gw-text-text-action hover:gw-text-text-action-hover gw-cursor-pointer"
            (click)="onSelectAllAcrossFilterClick()"
          >
            {{
              'common.bulk.banner.selectAllFilter'
                | translate : {total: totalRows}
            }}
          </button>
        </ng-container>
        <ng-container *ngSwitchCase="'all'">
          <span>
            {{
              'common.bulk.banner.allFilterSelected'
                | translate : {total: totalRows}
            }}
          </span>
          <button
            type="button"
            class="gw-border-0 gw-bg-transparent gw-p-0 gw-font-medium gw-text-text-action hover:gw-text-text-action-hover gw-cursor-pointer"
            (click)="clearSelection()"
          >
            {{ 'common.bulk.banner.clearSelection' | translate }}
          </button>
        </ng-container>
        <ng-container *ngSwitchDefault>
          <span class="gw-font-medium gw-text-text-primary">
            {{
              'common.bulk.confirm.selectedCount'
                | translate : {count: getSelectionCount()}
            }}
          </span>
          <button
            type="button"
            class="gw-border-0 gw-bg-transparent gw-p-0 gw-text-text-action hover:gw-text-text-action-hover gw-cursor-pointer"
            (click)="clearSelection()"
          >
            {{ 'common.bulk.banner.clearSelection' | translate }}
          </button>
        </ng-container>
      </ng-container>
    </div>

    <div
      *ngIf="hasBulkActions()"
      class="gw-flex gw-shrink-0 gw-flex-wrap gw-items-center gw-gap-2"
    >
      <ng-container
        *ngFor="let bAction of getBulkActions(); trackBy: trackByActionKey"
      >
        <button
          *ngIf="shouldShowBulkAction(bAction)"
          tuiButton
          type="button"
          appearance="flat"
          size="s"
          class="gw-inline-flex gw-items-center gw-gap-1.5"
          (click)="onBulkActionClick(bAction)"
          [attr.aria-label]="bAction.label || '' | translate"
          [class.gw-text-status-negative]="bAction.danger"
          [disabled]="isBulkActionDisabled(bAction)"
          [tuiHint]="bAction.label ? (bAction.label | translate) : null"
          tuiHintDirection="top"
        >
          <gatewee-icon
            [icon]="resolveActionIcon(bAction)"
            size="16px"
            iconStyle="primary"
            weight="bold"
          ></gatewee-icon>
        </button>
      </ng-container>
    </div>
  </div>

  <table
    tuiTable
    [size]="size"
    class="gw-w-full gw-rounded-lg gw-overflow-hidden"
    [ngClass]="'gw-table-size-' + size"
  >
    <thead>
      <tr>
        <th *ngIf="showExpandColumn && rowDetailTpl" tuiTh>
          <div [tuiCell]="size">
            <span tuiTitle class="gw-hidden sm:gw-inline">Expandir</span>
          </div>
        </th>

        <th *ngIf="hasCheckbox" tuiTh>
          <div [tuiCell]="size">
            <gatewee-checkbox
              size="s"
              [ngModel]="isHeaderCheckboxChecked()"
              [indeterminate]="isIndeterminate()"
              (ngModelChange)="checkUncheckAll()"
            ></gatewee-checkbox>
          </div>
        </th>

        <ng-container *ngFor="let col of columns; trackBy: trackByField">
          <th
            *ngIf="
              (col.alwaysVisible && col.field !== 'actions') ||
              (col.visible && col.field !== 'actions')
            "
            tuiTh
            [ngClass]="col.class"
          >
            <div [tuiCell]="size">
              <span tuiTitle *ngIf="col.label">
                <span class="gw-text-text-primary">{{
                  col?.label || '' | translate
                }}</span>
              </span>
            </div>
          </th>

          <th *ngIf="col.field === 'actions' && hasActions()" tuiTh>
            <div [tuiCell]="size">
              <span tuiTitle>{{ col.label || '' | translate }}</span>
            </div>
          </th>
        </ng-container>
      </tr>
    </thead>

    <tbody tuiTbody>
      <ng-container
        *ngFor="let row of data; let i = index; trackBy: trackByIndex"
      >
        <tr [ngClass]="getRowClass(row)">
          <td *ngIf="showExpandColumn && rowDetailTpl" tuiTd>
            <div [tuiCell]="size">
              <button
                type="button"
                tuiButton
                appearance="flat"
                size="s"
                [disabled]="!canExpandRow(row, i)"
                (click)="toggleExpand(row, i)"
                [attr.aria-expanded]="isExpanded(row, i)"
                [attr.aria-label]="isExpanded(row, i) ? 'Recolher' : 'Expandir'"
              >
                <gatewee-icon
                  [icon]="
                    isExpanded(row, i)
                      ? '@tui.chevron-down'
                      : '@tui.chevron-right'
                  "
                  size="16px"
                  weight="bold"
                ></gatewee-icon>
              </button>
            </div>
          </td>

          <td *ngIf="hasCheckbox" tuiTd>
            <div [tuiCell]="size">
              <gatewee-checkbox
                size="s"
                [ngModel]="isRowSelected(row, i)"
                (ngModelChange)="toggleRowSelection(row, i, $event)"
              ></gatewee-checkbox>
            </div>
          </td>

          <ng-container *ngFor="let col of columns; trackBy: trackByField">
            <td
              *ngIf="
                (col.alwaysVisible && col.field !== 'actions') ||
                (col.visible && col.field !== 'actions')
              "
              tuiTd
              [ngClass]="col.class"
            >
              <div [tuiCell]="size">
                <ng-container *ngIf="!col.template; else customTpl">
                  <ng-container *ngIf="col.badge; else plainValue">
                    <span
                      [tuiStatus]="getBadgeStatusColor(col, row)"
                      class="gw-text-text-secondary"
                    >
                      {{ getValueFromPath(row, col.field) | translate }}
                    </span>
                  </ng-container>

                  <ng-template #plainValue>
                    <ng-container *ngIf="col.hrefField; else noLink">
                      <a
                        [href]="getValueFromPath(row, col.hrefField)"
                        target="_blank"
                        rel="noopener noreferrer"
                        [ngClass]="col.class"
                        [class.gw-ellipsis]="col.ellipsed"
                        class="gw-text-text-action hover:gw-text-text-action-hover"
                      >
                        {{
                          getValueFromPath(row, col.field) || '-' | translate
                        }}
                      </a>
                    </ng-container>

                    <ng-template #noLink>
                      <ng-container
                        *ngIf="
                          isStructuredCellValue(
                            getValueFromPath(row, col.field, true)
                          );
                          else scalarCell
                        "
                      >
                        <button
                          type="button"
                          class="gw-cursor-pointer gw-text-xs gw-font-medium gw-text-text-action hover:gw-text-text-action-hover gw-underline"
                          (click)="
                            openStructuredValuePreview(
                              col.label,
                              getValueFromPath(row, col.field, true)
                            )
                          "
                        >
                          {{ 'common.actions.seeMore' | translate }}
                        </button>
                      </ng-container>

                      <ng-template #scalarCell>
                        <span
                          [ngClass]="col.class"
                          [class.gw-ellipsis]="col.ellipsed"
                          class="gw-text-text-primary"
                        >
                          {{
                            getValueFromPath(row, col.field) || '-' | translate
                          }}
                        </span>
                      </ng-template>
                    </ng-template>
                  </ng-template>
                </ng-container>

                <ng-template #customTpl>
                  <ng-container
                    *ngTemplateOutlet="
                      col.template;
                      context: {$implicit: row, col: col}
                    "
                  >
                  </ng-container>
                </ng-template>
              </div>
            </td>

            <td *ngIf="col.field === 'actions' && hasActions()" tuiTd>
              <div [tuiCell]="size">
                <div class="gw-flex gw-items-center gw-gap-2">
                  <ng-container
                    *ngFor="
                      let action of getActionsColumn()?.actions ?? [];
                      trackBy: trackByActionKey
                    "
                  >
                    <button
                      *ngIf="shouldShowAction(row, action)"
                      tuiIconButton
                      type="button"
                      appearance="flat"
                      size="s"
                      (click)="onActionClick(row, action)"
                      [attr.aria-label]="action.label || '' | translate"
                      [title]="action.label || '' | translate"
                      [class.gw-text-status-negative]="action.danger"
                      [disabled]="isActionDisabled(row, action)"
                    >
                      <gatewee-icon
                        [icon]="resolveActionIcon(action)"
                        size="16px"
                        iconStyle="primary"
                        weight="bold"
                      ></gatewee-icon>
                    </button>
                  </ng-container>
                </div>
              </div>
            </td>
          </ng-container>
        </tr>

        <tr *ngIf="isExpanded(row, i)">
          <td
            tuiTd
            [attr.colspan]="
              columns.length +
              (hasCheckbox ? 1 : 0) +
              (showExpandColumn ? 1 : 0)
            "
            class="gw-p-0"
          >
            <div class="gw-expand-wrapper">
              <ng-container
                *ngTemplateOutlet="rowDetailTpl; context: {$implicit: row}"
              ></ng-container>
            </div>
          </td>
        </tr>
      </ng-container>
    </tbody>
  </table>
</div>

<div
  class="gw-border-base gw-bg-background-elevation-2 gw-rounded-2xl gw-text-center gw-text-text-secondary gw-py-12"
  *ngIf="!data?.length"
>
  {{ 'common.dataNotFound' | translate }}
</div>

<div
  class="gw-mt-4 gw-flex gw-w-full gw-min-w-0 gw-flex-col gw-gap-2 gw-py-3"
  *ngIf="showPaginationFooter"
>
  <p
    class="gw-m-0 gw-text-right gw-text-sm gw-leading-snug gw-text-text-secondary"
  >
    {{ getSummary() }}
  </p>

  <div class="gw-flex gw-w-full gw-items-center gw-justify-between">
    <div
      class="gw-flex gw-shrink-0 gw-items-center gw-gap-2"
      *ngIf="showPaginationControls"
    >
      <span class="gw-text-sm gw-text-text-secondary gw-whitespace-nowrap">{{
        'common.rowsPerPage' | translate
      }}</span>
      <button
        tuiButton
        type="button"
        appearance="flat"
        tuiChevron
        class="gw-h-8 gw-min-w-[2.75rem] gw-shrink-0 gw-px-2 gw-text-sm gw-tabular-nums"
        [tuiDropdown]="pageSizeMenuTpl"
        [tuiDropdownMaxHeight]="280"
        [(tuiDropdownOpen)]="pageSizeMenuOpen"
      >
        {{ pageSize }}
      </button>
    </div>

    <div
      *ngIf="showPaginationControls && isCountKnown"
      class="gw-flex gw-items-center gw-justify-end gw-overflow-x-auto"
    >
      <tui-pagination
        [length]="totalPages"
        [index]="page - 1"
        (indexChange)="onPageIndexChange($event)"
      ></tui-pagination>
    </div>

    <div
      *ngIf="showPaginationControls && !isCountKnown"
      class="gw-ml-auto gw-flex gw-items-center gw-gap-2"
    >
      <button
        tuiButton
        type="button"
        appearance="flat"
        size="s"
        class="gw-h-8"
        [disabled]="!canGoPrev"
        (click)="onPrevPage()"
      >
        {{ 'common.actions.back' | translate }}
      </button>
      <span class="gw-text-sm gw-tabular-nums gw-text-text-secondary">
        {{ 'common.paginationPage' | translate : {page: page} }}
      </span>
      <button
        tuiButton
        type="button"
        appearance="flat"
        size="s"
        class="gw-h-8"
        [disabled]="!canGoNext"
        (click)="onNextPage()"
      >
        {{ 'common.actions.next' | translate }}
      </button>
    </div>
  </div>
</div>
